
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingData } from "@/types/onboarding";
import { useAuth } from "@/hooks/useAuth";
import { debugLogger } from '@/utils/debugLogger';

export const useSaveOnboardingStep = () => {
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const saveStepData = async (
    stepNumber: number, 
    data: OnboardingData
  ): Promise<boolean> => {
    if (!user) {
      debugLogger.error('No user found when trying to save step data');
      return false;
    }
    
    setSaving(true);
    const startTime = Date.now();
    
    try {
      // Enhanced data validation before save
      if (!data) {
        throw new Error('Dados do onboarding não encontrados');
      }

      // Get the specific data for this step
      const stepData = getStepData(stepNumber, data);
      
      debugLogger.debug(`Saving step ${stepNumber} data:`, {
        stepNumber,
        userId: user.id,
        stepData: stepData,
        timestamp: new Date().toISOString()
      });
      
      // Use UPSERT to handle missing profiles after deletion
      const { data: result, error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...stepData,
          last_onboarding_step: stepNumber,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        })
        .select('*');
      
      if (error) {
        debugLogger.error(`Database error saving step ${stepNumber}:`, {
          error: {
            message: error.message || 'Unknown database error',
            code: error.code || 'NO_CODE',
            details: error.details || 'No details',
            hint: error.hint || 'No hint'
          },
          stepNumber,
          userId: user.id,
          stepData: stepData,
          duration: Date.now() - startTime
        });
        throw new Error(error.message || 'Erro ao salvar dados no banco');
      }
      
      // Verify the data was actually saved
      if (!result || result.length === 0) {
        debugLogger.error(`No data returned after saving step ${stepNumber}`, {
          stepNumber,
          userId: user.id,
          result
        });
        throw new Error('Dados não foram salvos corretamente');
      }

      // Log successful save with verification
      debugLogger.debug(`Step ${stepNumber} saved successfully:`, {
        stepNumber,
        userId: user.id,
        savedData: result[0],
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      });
      
      return true;
    } catch (error: any) {
      const errorMessage = error?.message || 'Erro desconhecido ao salvar dados';
      const errorCode = error?.code || 'UNKNOWN_ERROR';
      
      debugLogger.error(`Exception saving step ${stepNumber}:`, {
        error: {
          message: errorMessage,
          code: errorCode,
          details: error?.details || 'No details',
          hint: error?.hint || 'No hint',
          stack: error?.stack || 'No stack trace'
        },
        stepNumber,
        userId: user.id,
        dataToSave: getStepData(stepNumber, data),
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      });
      
      // Re-throw with enhanced error information
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Simplified field processing - minimal filtering
  const processFields = (obj: any): any => {
    const processed: any = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      // Only exclude null and undefined
      if (value !== null && value !== undefined) {
        processed[key] = value;
      }
    });
    return processed;
  };

  // Extract relevant data for each step - ATUALIZADO com nova numeração (7 etapas)
  const getStepData = (stepNumber: number, data: OnboardingData): any => {
    try {
      switch (stepNumber) {
    case 1: // Username + Birthdate step
      const usernameData = { 
        username: data.username,
        birth_date: data.birthdate
        // age is calculated dynamically from birth_date
      };
          debugLogger.debug(`Step 1 data extraction:`, { 
            originalUsername: data.username,
            originalBirthdate: data.birthdate,
            originalAge: data.age,
            extracted: usernameData 
          });
          return processFields(usernameData);
        
        case 2: // Gender step
          const genderData = { 
            gender: data.gender,
            // Salvar username também aqui para garantir persistência
            ...(data.username && data.username.trim() && !data.username.startsWith('user_') ? { username: data.username } : {})
          };
          debugLogger.debug(`Step 2 data extraction:`, { originalGender: data.gender, extracted: genderData });
          return genderData;
        
        case 3: // Orientation step
          const orientationData = { 
            orientation: data.orientation,
            // Salvar username também aqui para garantir persistência
            ...(data.username && data.username.trim() && !data.username.startsWith('user_') ? { username: data.username } : {})
          };
          debugLogger.debug(`Step 3 data extraction:`, { originalOrientation: data.orientation, extracted: orientationData });
          return orientationData;
          
        case 4: // Role step
          const roleData = { 
            role: data.role,
            // Salvar username também aqui para garantir persistência
            ...(data.username && data.username.trim() && !data.username.startsWith('user_') ? { username: data.username } : {})
          };
          debugLogger.debug(`Step 4 data extraction:`, { 
            originalRole: data.role, 
            extracted: roleData,
            roleType: typeof data.role,
            roleValid: ['sponsor', 'sponsored'].includes(data.role)
          });
          return roleData;
          
        case 5: // Relationship Type step
          const relationshipData = { 
            relationship_type: data.relationshipType,
            seeking_casual_only: data.seekingCasualOnly,
            // Salvar username também aqui para garantir persistência
            ...(data.username && data.username.trim() && !data.username.startsWith('user_') ? { username: data.username } : {})
          };
          debugLogger.debug(`Step 5 data extraction:`, { originalRelationshipType: data.relationshipType, extracted: relationshipData });
          return relationshipData;
          
        case 6: // Geolocation step
          const geoData = { 
            latitude: data.geolocation?.latitude,
            longitude: data.geolocation?.longitude,
            city: data.location?.city,
            country: data.location?.country || 'BR',
            // State is already stored as code from StateSelect
            state_code: data.location?.state || null,
            // Salvar username também aqui para garantir persistência
            ...(data.username && data.username.trim() && !data.username.startsWith('user_') ? { username: data.username } : {})
          };
          return processFields(geoData);
          
        case 7: // Photos step (última etapa)
          const finalData: any = {};
          
          // SEMPRE garantir que username seja salvo se válido
          if (data.username && data.username.trim() && !data.username.startsWith('user_')) {
            finalData.username = data.username;
          }
          
          // Include any other missing critical data with null safety
          if (data.gender) finalData.gender = data.gender;
          if (data.orientation) finalData.orientation = data.orientation;
          if (data.role) finalData.role = data.role;
          if (data.relationshipType) finalData.relationship_type = data.relationshipType;
          if (data.birthdate) finalData.birth_date = data.birthdate;
          if (data.bio) finalData.bio = data.bio;
          if (data.education) finalData.education = data.education;
          if (data.occupation) finalData.occupation = data.occupation;
          if (data.interests && data.interests.length > 0) finalData.interests = data.interests;
          if (data.location?.city) finalData.city = data.location.city;
          if (data.location?.state) finalData.state_code = data.location.state.substring(0, 2).toUpperCase();
          if (data.geolocation?.latitude) finalData.latitude = data.geolocation.latitude;
          if (data.geolocation?.longitude) finalData.longitude = data.geolocation.longitude;
          if (data.shareIncome !== undefined) finalData.share_income = data.shareIncome;
          
          // Personal characteristics usando novas colunas inglesas com null safety
          if (data.height) finalData.height = data.height;
          if (data.bodyType) finalData.body_type = data.bodyType;
          if (data.hairColor) finalData.hair_color = data.hairColor;
          if (data.eyeColor) finalData.eye_color = data.eyeColor;
          if (data.smoker) finalData.smoker = data.smoker;
          if (data.drinker) finalData.drinker = data.drinker;
          if (data.piercings) finalData.piercings = data.piercings;
          if (data.tattoos) finalData.tattoos = data.tattoos;
          if (data.travelAvailability) finalData.travel_availability = data.travelAvailability;
          if (data.maritalStatus) finalData.marital_status = data.maritalStatus;
          if (data.sigilo_presenca) finalData.sigilo_presenca = data.sigilo_presenca;
          if (data.children) finalData.children = data.children;
          
          // Photos data with enhanced validation and EXPLICIT main_photo_url setting
          if (data.photos && Array.isArray(data.photos) && data.photos.length > 0) {
            finalData.profile_photos = data.photos;
            finalData.main_photo_url = data.photos[0]; // CRITICAL: Always set main_photo_url
            finalData.photo_version = 'v1'; // 🎯 FASE 1: All new photos use v1 structure
            
            debugLogger.debug('Step 7: Setting main_photo_url and photo_version:', {
              main_photo_url: finalData.main_photo_url,
              photo_version: finalData.photo_version,
              total_photos: data.photos.length,
              all_photos: data.photos
            });
          } else {
            debugLogger.warn('Step 7: No photos found in data', { photos: data.photos });
          }
          
          debugLogger.debug('Step 7 final data with main_photo_url:', finalData);
          return finalData;
          
        default:
          debugLogger.warn(`Unknown step number: ${stepNumber}`);
          return {};
      }
    } catch (extractionError: any) {
      debugLogger.error(`Error extracting data for step ${stepNumber}:`, {
        error: extractionError?.message || 'Unknown extraction error',
        stepNumber,
        data
      });
      throw new Error(`Erro ao processar dados do passo ${stepNumber}`);
    }
  };

  return {
    saveStepData,
    saving
  };
};
